
import React, { useState } from 'react';
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Lock, 
  Unlock, 
  Eye, 
  EyeOff,
  Search, 
  Plus, 
  MapPin,
  Store,
  Package,
  CreditCard,
  Phone,
  Mail,
  Building,
  Calendar,
  User,
  Shield,
  Star,
  LogIn,
  ChevronDown,
  ArrowUpDown,
  Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';

const Customers = () => {
  const { toast } = useToast();
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);
  const [addressTypeCreate, setAddressTypeCreate] = useState('new');
  const [addressTypeEdit, setAddressTypeEdit] = useState('new');
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [statementDateType, setStatementDateType] = useState('system');
  const [weekDay, setWeekDay] = useState('1');
  const [monthDay, setMonthDay] = useState('1');
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);
  
  // Form data states
  const [createFormData, setCreateFormData] = useState({
    username: '',
    password: '112233aA',
    fullName: '',
    email: '',
    phone: '',
    gender: '',
    birthDate: '',
    addressType: 'new',
    detailAddress: '',
    province: '',
    district: '',
    ward: '',
    notes: ''
  });

  // Mock data for customer details
  const [customerAddresses, setCustomerAddresses] = useState([
    {
      id: 'addr1',
      name: 'Nguyễn Văn A',
      phone: '0901234567',
      type: 'sender',
      addressType: 'new', // new hoặc old
      province: 'Hà Nội',
      district: 'Đống Đa',
      ward: 'Láng Hạ',
      detailedAddress: 'Số 123, Đường Láng',
      isDefault: true
    },
    {
      id: 'addr2',
      name: 'Công ty ABC',
      phone: '0901234568',
      type: 'recipient',
      addressType: 'old', // new hoặc old
      province: 'Hà Nội',
      district: 'Cầu Giấy',
      ward: 'Dịch Vọng',
      detailedAddress: 'Số 456, Phố Duy Tân',
      isDefault: false
    }
  ]);

  const [customerProducts, setCustomerProducts] = useState([
    {
      id: 'prod1',
      name: 'Điện thoại iPhone',
      category: 'Điện tử',
      defaultValue: 25000000,
      defaultWeight: 500, // gram
      dimensions: {
        length: 155, // mm
        width: 76,   // mm
        height: 8    // mm
      },
      isFrequent: true
    },
    {
      id: 'prod2',
      name: 'Quần áo thời trang',
      category: 'Thời trang',
      defaultValue: 500000,
      defaultWeight: 300, // gram
      dimensions: {
        length: 300, // mm
        width: 250,  // mm
        height: 50   // mm
      },
      isFrequent: false
    }
  ]);

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Đã sao chép",
        description: "Đã sao chép vào clipboard",
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast({
        title: "Lỗi",
        description: "Không thể sao chép",
        variant: "destructive"
      });
    }
  };

  const [customerBankAccounts, setCustomerBankAccounts] = useState([
    {
      id: 'bank1',
      bankName: 'Vietcombank',
      accountNumber: '1234567890',
      accountName: 'Nguyễn Văn A',
      branch: 'Hà Nội - Láng Hạ',
      isDefault: true
    },
    {
      id: 'bank2',
      bankName: 'Techcombank',
      accountNumber: '0987654321',
      accountName: 'Nguyễn Văn A',
      branch: 'Hà Nội - Cầu Giấy',
      isDefault: false
    }
  ]);

  // Mock data với nhiều thông tin hơn
  const [customers] = useState([
    {
      id: 'KH001',
      name: 'Nguyễn Văn A',
      email: 'nguyenvana@email.com',
      phone: '0901234567',
      registrationType: 'Cá nhân',
      hub: 'Hà Nội - Đống Đa',
      status: 'active',
      codInfo: {
        bankName: 'Vietcombank',
        accountNumber: '1234567890',
        accountName: 'Nguyễn Văn A'
      },
      address: 'Số 123, Đường Láng, P.Láng Hạ, Q.Đống Đa, Hà Nội',
      totalOrders: 156,
      totalRevenue: 45000000,
      addresses: 3,
      products: 8,
      bankAccounts: 2,
      createdAt: '2024-01-15T10:30:00+07:00'
    },
    {
      id: 'KH002',
      name: 'Trần Thị B',
      email: 'tranthib@email.com',
      phone: '0987654321',
      registrationType: 'Doanh nghiệp',
      hub: 'TP.HCM - Quận 1',
      status: 'active',
      codInfo: {
        bankName: 'Techcombank',
        accountNumber: '0987654321',
        accountName: 'Công ty TNHH ABC'
      },
      address: 'Số 456, Đường Nguyễn Huệ, P.Bến Nghé, Q.1, TP.HCM',
      totalOrders: 298,
      totalRevenue: 125000000,
      addresses: 5,
      products: 15,
      bankAccounts: 3,
      createdAt: '2024-02-20T14:15:00+07:00'
    },
    {
      id: 'KH003',
      name: 'Lê Văn C',
      email: 'levanc@email.com',
      phone: '0123456789',
      registrationType: 'Cá nhân',
      hub: 'Đà Nẵng - Hải Châu',
      status: 'locked',
      codInfo: {
        bankName: 'MB Bank',
        accountNumber: '5555666677',
        accountName: 'Lê Văn C'
      },
      address: 'Số 789, Đường Trần Phú, P.Thạch Thang, Q.Hải Châu, Đà Nẵng',
      totalOrders: 45,
      totalRevenue: 8500000,
      addresses: 2,
      products: 3,
      bankAccounts: 1,
      createdAt: '2024-03-10T09:45:00+07:00'
    },
    {
      id: 'KH004',
      name: 'Phạm Thị D',
      email: 'phamthid@email.com',
      phone: '0369258147',
      registrationType: 'Doanh nghiệp',
      hub: 'Hà Nội - Cầu Giấy',
      status: 'active',
      codInfo: {
        bankName: 'ACB',
        accountNumber: '7777888899',
        accountName: 'Công ty XYZ'
      },
      address: 'Số 321, Đường Cầu Giấy, P.Dịch Vọng, Q.Cầu Giấy, Hà Nội',
      totalOrders: 189,
      totalRevenue: 67000000,
      addresses: 4,
      products: 12,
      bankAccounts: 2,
      createdAt: '2024-04-05T16:20:00+07:00'
    }
  ]);

  const [users] = useState([
    {
      id: 'NV001',
      name: 'Lê Văn C',
      email: 'levanc@company.com',
      phone: '0987654321',
      role: 'admin',
      status: 'active',
      createdAt: '2024-11-01T09:00:00+07:00',
      lastLogin: '2025-01-12T08:30:00+07:00',
      department: 'Quản lý'
    },
    {
      id: 'NV002',
      name: 'Phạm Thị D',
      email: 'phamthid@company.com',
      phone: '0123456789',
      role: 'manager',
      status: 'active',
      createdAt: '2024-12-10T10:00:00+07:00',
      lastLogin: '2025-01-12T09:15:00+07:00',
      department: 'Vận hành'
    },
    {
      id: 'NV003',
      name: 'Hoàng Văn E',
      email: 'hoangvane@company.com',
      phone: '0369852147',
      role: 'staff',
      status: 'locked',
      createdAt: '2024-10-05T11:30:00+07:00',
      lastLogin: '2025-01-10T16:45:00+07:00',
      department: 'Hỗ trợ'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'locked':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getTextColorByStatus = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-700 dark:text-green-300';
      case 'locked':
        return 'text-red-700 dark:text-red-300';
      case 'pending':
        return 'text-yellow-700 dark:text-yellow-300';
      default:
        return 'text-foreground';
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'admin': return 'Quản trị viên';
      case 'manager': return 'Quản lý';
      case 'staff': return 'Nhân viên';
      default: return 'Khách hàng';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'manager': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'staff': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const handleAccessCustomer = (customerId) => {
    toast({
      title: "Truy cập tài khoản",
      description: `Đang truy cập vào tài khoản khách hàng ${customerId}`,
    });
  };

  const handleViewProfile = (customer) => {
    toast({
      title: "Xem hồ sơ",
      description: `Đang xem hồ sơ của ${customer.name}`,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Quản lý khách hàng</h1>
          <p className="text-muted-foreground">Quản lý thông tin khách hàng và người dùng hệ thống</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm khách hàng
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-full w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Thêm khách hàng mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Thông tin tài khoản */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Thông tin tài khoản</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Tên đăng nhập *</Label>
                    <Input 
                      value={createFormData.username}
                      onChange={(e) => setCreateFormData({...createFormData, username: e.target.value})}
                      placeholder="Nhập tên đăng nhập" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Mật khẩu *</Label>
                    <div className="relative">
                      <Input 
                        type={showPassword ? "text" : "password"} 
                        value={createFormData.password}
                        onChange={(e) => setCreateFormData({...createFormData, password: e.target.value})}
                        placeholder="Nhập mật khẩu" 
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Thông tin cá nhân */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Thông tin cá nhân</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Tên đầy đủ *</Label>
                    <Input 
                      value={createFormData.fullName}
                      onChange={(e) => setCreateFormData({...createFormData, fullName: e.target.value})}
                      placeholder="Nhập họ và tên đầy đủ" 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        type="email" 
                        value={createFormData.email}
                        onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                        placeholder="Nhập email" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Số điện thoại</Label>
                      <Input 
                        value={createFormData.phone}
                        onChange={(e) => setCreateFormData({...createFormData, phone: e.target.value})}
                        placeholder="Nhập số điện thoại" 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gender">Giới tính</Label>
                      <Select 
                        value={createFormData.gender}
                        onValueChange={(value) => setCreateFormData({...createFormData, gender: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Chọn giới tính" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Nam</SelectItem>
                          <SelectItem value="female">Nữ</SelectItem>
                          <SelectItem value="other">Khác</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="birthDate">Ngày sinh</Label>
                      <Input 
                        type="date" 
                        value={createFormData.birthDate}
                        onChange={(e) => setCreateFormData({...createFormData, birthDate: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Địa chỉ */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Địa chỉ</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="addressType">Loại địa chỉ *</Label>
                    <Select 
                      value={addressTypeCreate} 
                      onValueChange={(value) => {
                        setAddressTypeCreate(value);
                        setCreateFormData({...createFormData, addressType: value});
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn loại địa chỉ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="old">Địa chỉ cũ (Phường/Xã | Quận/Huyện | Thành phố | Tỉnh)</SelectItem>
                        <SelectItem value="new">Địa chỉ mới (Phường/Xã | Thành phố/Tỉnh)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="detailAddress">Địa chỉ chi tiết *</Label>
                    <Input 
                      value={createFormData.detailAddress}
                      onChange={(e) => setCreateFormData({...createFormData, detailAddress: e.target.value})}
                      placeholder="Số nhà, tên đường..." 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="province">Tỉnh/Thành phố *</Label>
                      <Select 
                        value={createFormData.province}
                        onValueChange={(value) => setCreateFormData({...createFormData, province: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Chọn tỉnh/thành" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hanoi">Hà Nội</SelectItem>
                          <SelectItem value="hcm">TP. Hồ Chí Minh</SelectItem>
                          <SelectItem value="danang">Đà Nẵng</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {addressTypeCreate === 'old' && (
                      <div className="space-y-2">
                        <Label htmlFor="district">Quận/Huyện</Label>
                        <Select
                          value={createFormData.district}
                          onValueChange={(value) => setCreateFormData({...createFormData, district: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn quận/huyện" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dongda">Đống Đa</SelectItem>
                            <SelectItem value="caugiay">Cầu Giấy</SelectItem>
                            <SelectItem value="haidong">Hà Đông</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ward">Phường/Xã *</Label>
                    <Select
                      value={createFormData.ward}
                      onValueChange={(value) => setCreateFormData({...createFormData, ward: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn phường/xã" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="langha">Láng Hạ</SelectItem>
                        <SelectItem value="langthuong">Láng Thượng</SelectItem>
                        <SelectItem value="khuongmai">Khương Mai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ghi chú */}
              <div className="space-y-2">
                <Label htmlFor="notes">Ghi chú</Label>
                <Textarea 
                  value={createFormData.notes}
                  onChange={(e) => setCreateFormData({...createFormData, notes: e.target.value})}
                  placeholder="Nhập ghi chú về khách hàng..." 
                  rows={2} 
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateDialog(false);
                    setCreateFormData({
                      username: '',
                      password: '112233aA',
                      fullName: '',
                      email: '',
                      phone: '',
                      gender: '',
                      birthDate: '',
                      addressType: 'new',
                      detailAddress: '',
                      province: '',
                      district: '',
                      ward: '',
                      notes: ''
                    });
                    setAddressTypeCreate('new');
                    setShowPassword(false);
                  }}
                >
                  Hủy
                </Button>
                <Button 
                  onClick={() => {
                    toast({
                      title: "Thành công",
                      description: "Tạo khách hàng thành công!",
                    });
                    setShowCreateDialog(false);
                    setCreateFormData({
                      username: '',
                      password: '112233aA',
                      fullName: '',
                      email: '',
                      phone: '',
                      gender: '',
                      birthDate: '',
                      addressType: 'new',
                      detailAddress: '',
                      province: '',
                      district: '',
                      ward: '',
                      notes: ''
                    });
                    setAddressTypeCreate('new');
                    setShowPassword(false);
                  }}
                >
                  Tạo khách hàng
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng khách hàng</p>
                <p className="text-2xl font-bold">{customers.length}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Hoạt động</p>
                <p className="text-2xl font-bold text-green-600">
                  {customers.filter(c => c.status === 'active').length}
                </p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng đơn hàng</p>
                <p className="text-2xl font-bold">
                  {customers.reduce((sum, c) => sum + c.totalOrders, 0)}
                </p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tổng doanh thu</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(customers.reduce((sum, c) => sum + c.totalRevenue, 0))}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm khách hàng theo tên, email, số điện thoại..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card className="border-0 shadow-sm">
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Tên
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Mã khách hàng
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Liên hệ</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Loại đăng ký
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Bưu cục
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Thông tin nhận COD</TableHead>
                  <TableHead className="w-[200px] text-center sticky right-0 bg-background z-10 border-l">
                    Thao tác
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div 
                        className="space-y-1 cursor-pointer relative"
                        onMouseEnter={() => setHoveredField(`name-${customer.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(customer.name)}
                      >
                        <div className={`font-medium ${getTextColorByStatus(customer.status)} flex items-center gap-2`}>
                          {customer.name}
                          {hoveredField === `name-${customer.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="cursor-pointer relative"
                        onMouseEnter={() => setHoveredField(`id-${customer.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(customer.id)}
                      >
                        <div className={`font-mono text-sm ${getTextColorByStatus(customer.status)} flex items-center gap-2`}>
                          {customer.id}
                          {hoveredField === `id-${customer.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div 
                          className="flex items-center gap-1 text-sm cursor-pointer"
                          onMouseEnter={() => setHoveredField(`email-${customer.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(customer.email)}
                        >
                          <Mail className="h-3 w-3" />
                          {customer.email}
                          {hoveredField === `email-${customer.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground ml-1" />
                          )}
                        </div>
                        <div 
                          className="flex items-center gap-1 text-sm cursor-pointer"
                          onMouseEnter={() => setHoveredField(`phone-${customer.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(customer.phone)}
                        >
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                          {hoveredField === `phone-${customer.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground ml-1" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={customer.registrationType === 'Doanh nghiệp' ? 'default' : 'secondary'}>
                        {customer.registrationType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Building className="h-3 w-3" />
                        {customer.hub}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium">{customer.codInfo.bankName}</div>
                        <div 
                          className="text-muted-foreground cursor-pointer flex items-center gap-1"
                          onMouseEnter={() => setHoveredField(`account-${customer.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(customer.codInfo.accountNumber)}
                        >
                          STK: {customer.codInfo.accountNumber}
                          {hoveredField === `account-${customer.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <div className="text-muted-foreground">{customer.codInfo.accountName}</div>
                      </div>
                    </TableCell>
                    <TableCell className="sticky right-0 bg-background z-10 border-l">
                      <div className="flex items-center gap-1 justify-center">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setSelectedCustomer(customer)}
                          title="Xem hồ sơ khách hàng"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setSelectedCustomer(customer)}
                              title="Chỉnh sửa thông tin"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-full w-[95vw] max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Chỉnh sửa thông tin khách hàng - {customer.name}</DialogTitle>
                            </DialogHeader>
                            <div className="p-4 text-center">
                              <p>Form chỉnh sửa sẽ được hoàn thiện...</p>
                              <Button 
                                variant="outline" 
                                onClick={() => setShowEditDialog(false)}
                                className="mt-4"
                              >
                                Đóng
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            toast({
                              title: "Truy cập tài khoản",
                              description: `Đang truy cập vào tài khoản của ${customer.name}...`,
                            });
                          }}
                          title="Truy cập vào tài khoản"
                        >
                          <LogIn className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowChangePasswordDialog(true);
                          }}
                          title="Đổi mật khẩu"
                        >
                          <Shield className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Customer Profile Dialog */}
      <Dialog open={selectedCustomer !== null && !showEditDialog && !showChangePasswordDialog} onOpenChange={(open) => {
        if (!open) {
          setSelectedCustomer(null);
          setEditingAddress(null);
          setEditingProduct(null);
          setEditingBankAccount(null);
        }
      }}>
        <DialogContent className="max-w-full w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Hồ sơ khách hàng - {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6">
              {/* Thông tin cơ bản */}
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-medium">Mã khách hàng</Label>
                      <p className="text-sm">{selectedCustomer.id}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Họ và tên</Label>
                      <p className="text-sm">{selectedCustomer.name}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Email</Label>
                      <p className="text-sm">{selectedCustomer.email}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Số điện thoại</Label>
                      <p className="text-sm">{selectedCustomer.phone}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Địa chỉ</Label>
                      <p className="text-sm">{selectedCustomer.address}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Trạng thái</Label>
                      <Badge className={getStatusColor(selectedCustomer.status)}>
                        {selectedCustomer.status === 'active' ? 'Hoạt động' : 'Bị khóa'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Ngày tạo</Label>
                      <p className="text-sm">{new Date(selectedCustomer.createdAt).toLocaleString('vi-VN')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Tabs defaultValue="addresses" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="addresses" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Địa chỉ
                  </TabsTrigger>
                  <TabsTrigger value="products" className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Sản phẩm
                  </TabsTrigger>
                  <TabsTrigger value="banking" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Ngân hàng
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="addresses">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Quản lý địa chỉ</CardTitle>
                        <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
                          <DialogTrigger asChild>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Thêm địa chỉ
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Thêm địa chỉ mới</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Tên</Label>
                                  <Input placeholder="Tên người nhận/gửi" />
                                </div>
                                <div className="space-y-2">
                                  <Label>Số điện thoại</Label>
                                  <Input placeholder="Số điện thoại" />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Loại địa chỉ</Label>
                                <Select>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Chọn loại" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="sender">Người gửi</SelectItem>
                                    <SelectItem value="recipient">Người nhận</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Loại cấp độ địa chỉ</Label>
                                <Select>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Chọn cấp độ" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">2 cấp (Phường/Xã - Tỉnh/TP)</SelectItem>
                                    <SelectItem value="old">3 cấp (Phường/Xã - Quận/Huyện - Tỉnh/TP)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <Label>Tỉnh/TP</Label>
                                  <Select>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Chọn tỉnh" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="hanoi">Hà Nội</SelectItem>
                                      <SelectItem value="hcm">Hồ Chí Minh</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Quận/Huyện</Label>
                                  <Select>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Chọn quận" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="dongda">Đống Đa</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Phường/Xã</Label>
                                  <Select>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Chọn xã" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="langha">Láng Hạ</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Địa chỉ chi tiết</Label>
                                <Input placeholder="Số nhà, tên đường" />
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox id="default" />
                                <Label htmlFor="default">Đặt làm địa chỉ mặc định</Label>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setShowAddressDialog(false)}>
                                  Hủy
                                </Button>
                                <Button onClick={() => {
                                  setShowAddressDialog(false);
                                  toast({
                                    title: "Thành công",
                                    description: "Thêm địa chỉ thành công!",
                                  });
                                }}>
                                  Lưu
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {customerAddresses.map((address) => (
                          <div key={address.id} className="p-4 border rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{address.name}</span>
                                  <Badge variant={address.type === 'sender' ? 'default' : 'secondary'}>
                                    {address.type === 'sender' ? 'Người gửi' : 'Người nhận'}
                                  </Badge>
                                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                                    {address.addressType === 'new' ? '2 cấp' : '3 cấp'}
                                  </Badge>
                                  {address.isDefault && (
                                    <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                      <Star className="h-3 w-3 mr-1" />
                                      Mặc định
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{address.phone}</p>
                                <p className="text-sm">
                                  {address.detailedAddress}, {address.ward}, {address.addressType === 'old' && address.district + ', '}{address.province}
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <Button size="sm" variant="outline" onClick={() => {
                                  setEditingAddress(address);
                                  setShowAddressDialog(true);
                                }}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                  if (confirm('Bạn có chắc chắn muốn xóa địa chỉ này?')) {
                                    setCustomerAddresses(customerAddresses.filter(addr => addr.id !== address.id));
                                    toast({
                                      title: "Thành công",
                                      description: "Đã xóa địa chỉ thành công!",
                                    });
                                  }
                                }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="products">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Quản lý sản phẩm</CardTitle>
                        <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                          <DialogTrigger asChild>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Thêm sản phẩm
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Thêm sản phẩm mới</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label>Tên sản phẩm</Label>
                                <Input placeholder="Tên sản phẩm" />
                              </div>
                              <div className="space-y-2">
                                <Label>Danh mục</Label>
                                <Select>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Chọn danh mục" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="electronics">Điện tử</SelectItem>
                                    <SelectItem value="fashion">Thời trang</SelectItem>
                                    <SelectItem value="books">Sách</SelectItem>
                                    <SelectItem value="food">Thực phẩm</SelectItem>
                                    <SelectItem value="other">Khác</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Giá trị mặc định (VNĐ)</Label>
                                  <Input type="number" placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                  <Label>Cân nặng mặc định (gram)</Label>
                                  <Input type="number" step="1" placeholder="0" />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Kích thước (mm)</Label>
                                <div className="grid grid-cols-3 gap-2">
                                  <Input type="number" step="0.1" placeholder="Dài (mm)" />
                                  <Input type="number" step="0.1" placeholder="Rộng (mm)" />
                                  <Input type="number" step="0.1" placeholder="Cao (mm)" />
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox id="frequent" />
                                <Label htmlFor="frequent">Đánh dấu là sản phẩm thường dùng</Label>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setShowProductDialog(false)}>
                                  Hủy
                                </Button>
                                <Button onClick={() => {
                                  setShowProductDialog(false);
                                  toast({
                                    title: "Thành công",
                                    description: "Thêm sản phẩm thành công!",
                                  });
                                }}>
                                  Lưu
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {customerProducts.map((product) => (
                          <div key={product.id} className="p-4 border rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium">{product.name}</h4>
                                <Badge variant="secondary" className="text-xs mt-1">
                                  {product.category}
                                </Badge>
                              </div>
                              {product.isFrequent && (
                                <Badge variant="secondary" className="text-xs">
                                  <Star className="h-3 w-3 mr-1" />
                                  Thường dùng
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              <div>Giá trị: {product.defaultValue.toLocaleString()}đ</div>
                              <div>Cân nặng: {product.defaultWeight}g</div>
                              <div>Kích thước: {product.dimensions.length} x {product.dimensions.width} x {product.dimensions.height} mm</div>
                            </div>
                            <div className="flex space-x-2 mt-3">
                              <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                                setEditingProduct(product);
                                setShowProductDialog(true);
                              }}>
                                <Edit className="h-4 w-4 mr-1" />
                                Sửa
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => {
                                if (confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) {
                                  setCustomerProducts(customerProducts.filter(prod => prod.id !== product.id));
                                  toast({
                                    title: "Thành công",
                                    description: "Đã xóa sản phẩm thành công!",
                                  });
                                }
                              }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="banking">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Thông tin ngân hàng</CardTitle>
                        <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
                          <DialogTrigger asChild>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Thêm tài khoản
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Thêm tài khoản ngân hàng</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label>Tên ngân hàng</Label>
                                <Select>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Chọn ngân hàng" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="vcb">Vietcombank</SelectItem>
                                    <SelectItem value="tcb">Techcombank</SelectItem>
                                    <SelectItem value="mb">MB Bank</SelectItem>
                                    <SelectItem value="acb">ACB</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Số tài khoản</Label>
                                <Input placeholder="Nhập số tài khoản" />
                              </div>
                              <div className="space-y-2">
                                <Label>Tên chủ tài khoản</Label>
                                <Input placeholder="Tên chủ tài khoản" />
                              </div>
                              <div className="space-y-2">
                                <Label>Chi nhánh</Label>
                                <Input placeholder="Tên chi nhánh ngân hàng" />
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox id="defaultBank" />
                                <Label htmlFor="defaultBank">Đặt làm tài khoản mặc định để nhận COD</Label>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <Button variant="outline" onClick={() => setShowBankDialog(false)}>
                                  Hủy
                                </Button>
                                <Button onClick={() => {
                                  setShowBankDialog(false);
                                  toast({
                                    title: "Thành công",
                                    description: "Thêm tài khoản ngân hàng thành công!",
                                  });
                                }}>
                                  Lưu
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {customerBankAccounts.map((account) => (
                          <div key={account.id} className="p-4 border rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{account.bankName}</span>
                                  {account.isDefault && (
                                    <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                      <Star className="h-3 w-3 mr-1" />
                                      Mặc định
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  STK: {account.accountNumber}
                                </p>
                                <p className="text-sm">
                                  Chủ TK: {account.accountName}
                                </p>
                                <p className="text-sm">
                                  Chi nhánh: {account.branch}
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <Button size="sm" variant="outline" onClick={() => {
                                  setEditingBankAccount(account);
                                  setShowBankDialog(true);
                                }}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                  if (confirm('Bạn có chắc chắn muốn xóa tài khoản ngân hàng này?')) {
                                    setCustomerBankAccounts(customerBankAccounts.filter(acc => acc.id !== account.id));
                                    toast({
                                      title: "Thành công",
                                      description: "Đã xóa tài khoản ngân hàng thành công!",
                                    });
                                  }
                                }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowChangePasswordDialog(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Đổi mật khẩu
                  </Button>
                  <Button size="sm" variant="outline">
                    <Lock className="h-4 w-4 mr-2" />
                    {selectedCustomer.status === 'active' ? 'Khóa tài khoản' : 'Mở khóa'}
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
                  Đóng
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          // Reset editing state when closing
          setTimeout(() => {
            if (!showChangePasswordDialog) {
              setSelectedCustomer(null);
            }
          }, 100);
        }
      }}>
        <DialogContent className="max-w-full w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa thông tin khách hàng - {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Thông tin cơ bản */}
            <div className="border-b pb-4">
              <h3 className="font-medium mb-3">Thông tin cơ bản</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editFullName">Tên đầy đủ *</Label>
                  <Input defaultValue={selectedCustomer?.name || ''} placeholder="Nhập họ và tên đầy đủ" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editEmail">Email</Label>
                  <Input type="email" defaultValue={selectedCustomer?.email || ''} placeholder="Nhập email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editPhone">Số điện thoại</Label>
                  <Input defaultValue={selectedCustomer?.phone || ''} placeholder="Nhập số điện thoại" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editGender">Giới tính</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn giới tính" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Nam</SelectItem>
                      <SelectItem value="female">Nữ</SelectItem>
                      <SelectItem value="other">Khác</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editBirthDate">Ngày sinh</Label>
                  <Input type="date" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editCccd">Số CCCD</Label>
                  <Input placeholder="Nhập số CCCD" />
                </div>
              </div>
            </div>

            {/* Địa chỉ */}
            <div className="border-b pb-4">
              <h3 className="font-medium mb-3">Địa chỉ</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="editAddressType">Loại địa chỉ</Label>
                  <Select 
                    value={addressTypeEdit} 
                    onValueChange={setAddressTypeEdit}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại địa chỉ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="old">Địa chỉ cũ (Phường/Xã | Quận/Huyện | Thành phố | Tỉnh)</SelectItem>
                      <SelectItem value="new">Địa chỉ mới (Phường/Xã | Thành phố/Tỉnh)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editDetailAddress">Địa chỉ chi tiết</Label>
                  <Input defaultValue={selectedCustomer?.address || ''} placeholder="Số nhà, tên đường..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editProvince">Tỉnh/Thành phố</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn tỉnh/thành" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hanoi">Hà Nội</SelectItem>
                        <SelectItem value="hcm">TP. Hồ Chí Minh</SelectItem>
                        <SelectItem value="danang">Đà Nẵng</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {addressTypeEdit === 'old' && (
                    <div className="space-y-2">
                      <Label htmlFor="editDistrict">Quận/Huyện</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Chọn quận" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dongda">Đống Đa</SelectItem>
                          <SelectItem value="caugiay">Cầu Giấy</SelectItem>
                          <SelectItem value="haidong">Hà Đông</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editWard">Phường/Xã</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn phường/xã" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="langha">Láng Hạ</SelectItem>
                      <SelectItem value="langthuong">Láng Thượng</SelectItem>
                      <SelectItem value="khuongmai">Khương Mai</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Thông tin bổ sung */}
            <div className="border-b pb-4">
              <h3 className="font-medium mb-3">Thông tin bổ sung</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editTaxCode">Mã số thuế</Label>
                  <Input placeholder="Nhập mã số thuế" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editStatementDate">Ngày chốt sao kê</Label>
                  <Select value={statementDateType} onValueChange={setStatementDateType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn loại chốt sao kê" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">Theo hệ thống (mặc định)</SelectItem>
                      <SelectItem value="weekly">Hàng tuần</SelectItem>
                      <SelectItem value="monthly">Hàng tháng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {statementDateType === 'weekly' && (
                  <div className="space-y-2">
                    <Label htmlFor="weekDay">Thứ trong tuần</Label>
                    <Select value={weekDay} onValueChange={setWeekDay}>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn thứ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Thứ 2</SelectItem>
                        <SelectItem value="2">Thứ 3</SelectItem>
                        <SelectItem value="3">Thứ 4</SelectItem>
                        <SelectItem value="4">Thứ 5</SelectItem>
                        <SelectItem value="5">Thứ 6</SelectItem>
                        <SelectItem value="6">Thứ 7</SelectItem>
                        <SelectItem value="0">Chủ nhật</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {statementDateType === 'monthly' && (
                  <div className="space-y-2">
                    <Label htmlFor="monthDay">Ngày trong tháng</Label>
                    <Select value={monthDay} onValueChange={setMonthDay}>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn ngày" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={day.toString()}>
                            Ngày {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="editAccountStatus">Trạng thái tài khoản</Label>
                  <Select defaultValue={selectedCustomer?.status || ''}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn trạng thái" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Hoạt động</SelectItem>
                      <SelectItem value="locked">Bị khóa</SelectItem>
                      <SelectItem value="pending">Chờ xác thực</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editVerificationStatus">Trạng thái xác thực</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn trạng thái xác thực" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified">Đã xác thực</SelectItem>
                      <SelectItem value="pending">Chờ xác thực</SelectItem>
                      <SelectItem value="rejected">Từ chối</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Ghi chú */}
            <div className="space-y-2">
              <Label htmlFor="editNotes">Ghi chú</Label>
              <Textarea placeholder="Nhập ghi chú về khách hàng..." rows={3} />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Hủy</Button>
              <Button 
                onClick={() => {
                  setShowEditDialog(false);
                  toast({
                    title: "Thành công",
                    description: "Cập nhật thông tin khách hàng thành công!",
                  });
                }}
              >
                Cập nhật thông tin
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={(open) => {
        setShowChangePasswordDialog(open);
        if (!open) {
          // Reset state when closing
          setTimeout(() => {
            if (!showEditDialog) {
              setSelectedCustomer(null);
            }
          }, 100);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Đổi mật khẩu khách hàng</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Mật khẩu mới</Label>
              <div className="relative">
                <Input 
                  name="new_password"
                  type={showPasswordEdit ? "text" : "password"} 
                  placeholder="Nhập mật khẩu mới"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPasswordEdit(!showPasswordEdit)}
                >
                  {showPasswordEdit ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="changePasswordReason">Lý do đổi mật khẩu *</Label>
              <Textarea 
                placeholder="Nhập lý do đổi mật khẩu (bắt buộc)..." 
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowChangePasswordDialog(false)}>
                Hủy
              </Button>
              <Button onClick={() => {
                setShowChangePasswordDialog(false);
                toast({
                  title: "Thành công", 
                  description: "Đổi mật khẩu thành công!",
                });
              }}>
                Đổi mật khẩu
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
